import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import { deleteDoc } from "@firebase/firestore";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
    Grid,
    MenuItem,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import styles from "../../components/DataTable/Sheets.module.css";
import Loading from "../../components/Loading";
import { StyledTextField } from "../../components/Styles/styledTextField";

import firebase, { collection, db, doc, getDoc, updateDoc, } from "../../service/firebase";
import { eurocurrencies, updateCurrencyColumn, } from "../NewPopstore/NewPopstore";
import { DeleteAlert, ErrorAlert, SuccessAlert, } from "../../components/Styles/styledNotificationAlerts";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { getOrderService, getOrderTotal, getPackingPageOrders } from "../../service/order-service";
import { getCustomerAndOrders } from "../../service/customer-service";
import ToggleOn from "../../icons/toggle-on";
import ToggleOff from "../../icons/toggle-off";

const EditPopstore = () => {
    const {t} = useTranslation()
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const {storeId} = useParams();
    const [user, setUser] = useState();
    const [store, setStore] = useState();
    const [loading, setLoading] = useState(true);
    const [strefRef, setStrefRef] = useState(true);
    const [isSnackbarOpen, setSnackbarOpen] = React.useState(false);
    const [alert, setAlert] = useState({type: "", message: ""});
    const [openModal, setOpenModal] = useState(false);
    const [lockStore, setLockStore] = useState(false)
    const handleAlert = async (type, message) => {
        await setAlert({type, message});
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleOpenModal = async (e) => {
        setOpenModal(true);
    };

    // Custom toast style
    const customToastStyle = {
        background: "#4c8991",
        color: "white",
    };

    // Show custom toast
    const showCustomToast = ({msg}) => {
        toast(msg, {
            style: customToastStyle,
        });
    };

    useEffect(() => {
        firebase.auth().onAuthStateChanged(async (user) => {
            if (user) {
                setUser(user);
                const storesRef = await collection(db, `/StoreOwners/${user.uid}/allStores`);
                const store = await getDoc(doc(storesRef, storeId));
                if (store.exists()) {
                    let data = store.data();
                    data.columnsList = JSON.parse(data.columnsList);
                    setStore(data);
                    setLockStore(data.locked)
                    setLoading(false);
                    setStrefRef(storesRef);
                }
            } else {
                navigate("/");
            }

            localStorage.setItem(
                "columns",
                JSON.stringify({
                    Name: -1,
                    "Reference ID": -1,
                    Price: -1,
                    Ignore: 9,
                })
            );
        });
    }, [navigate, storeId]);

    const updateStore = async (e) => {
        e.preventDefault();

        if (store.storeName.trim() === "") {
            await handleAlert("error", t("Please select a name for PopStore"));
            handleOpenModal();
            return;
        }

        if (!isEmail(store.storeOwner) || store.storeOwner.trim() === "") {
            await handleAlert("error", "Please add an email for PopStore owner");
            handleOpenModal();
            return;
        }

        if (store.description.trim() === "") {
            await handleAlert("error", t("Please add description for PopStore"));
            handleOpenModal();
            return;
        }

        let updatedStore = {
            storeName: store.storeName,
            storeOwner: store.storeOwner,
            description: store.description,
            locked: lockStore,
            currency: store.currency,
        };

        const storesRef = await collection(db, `/StoreOwners/${user.uid}/allStores`);
        const storeRef = await doc(storesRef, storeId);

        await updateDoc(storeRef, updatedStore);

        await handleAlert("success", t("PopStore updated successfully"));
        handleOpenModal();

        localStorage.removeItem("columns");
    };

    const cancelStore = async (e) => {
        e.preventDefault();
        localStorage.removeItem("columns");
        navigate("/");
    };
    const cancelStorePopup = async (e) => {
        e.preventDefault();
        localStorage.removeItem("columns");
        handleCloseModal();
    };

    const handleCopy = (link) => {
        const url = `${window.location.origin}${link}`;
        // Copy the URL to the clipboard
        navigator.clipboard.writeText(url);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleDeletePopup = () => {
        handleAlert("delete", t("Are you sure you want to delete this popstore?"));
        handleOpenModal();
    };

    const handleDelete = async () => {
        try {
            const storeRef = doc(strefRef, storeId);
            await deleteDoc(storeRef);
            localStorage.removeItem("columns");
            setLoading(true)
            // showCustomToast({msg: "Store Deleted Successfully"});
            navigate("/");
        } catch (error) {
            console.log("Error opening delete popup:", error);
            // Handle the error condition here, if necessary
        }
    };

    // Generating pdf
    const generatePDF = (e) => {
        const doc = new jsPDF();
        // Add store name and owner
        doc.setFontSize(18);
        doc.text(store.storeName, 10, 20);

        doc.setFontSize(12);
        doc.text(`Owner: ${store.storeOwner}`, 10, 30);

        doc.text("Products", 10, 40);
        // Add table headers
        doc.setFontSize(10);
        doc.setTextColor("#000");
        doc.setFont(undefined, "bold");
        doc.text("Ref", 10, 45);
        doc.text("Name", 50, 45);
        doc.text("Price", 100, 45);

        // Add the table rows
        let y = 50;
        store.columnsList.forEach((row) => {
            doc.setFontSize(10);
            doc.setTextColor("#000");
            doc.setFont(undefined, "normal");
            doc.text(row[0], 10, y);
            doc.text(row[1], 50, y);
            doc.text(row[2], 100, y);
            y += 5;
        });

        y += 8
        doc.setFontSize(12);
        doc.text('Orders', 10, y);

        y += 5
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text("Product", 10, y);
        doc.text("Price", 50, y);
        doc.text("Quantity", 100, y);
        doc.text("Total", 150, y);

        y += 5
        doc.setFont(undefined, "normal");
        getOrderService(user.uid, storeId).then((res) => {
            res.map((order, index) => {
                doc.text(store.columnsList[order.id][1], 10, y);
                doc.text(store.columnsList[order.id][2] + ' ' + store?.currency, 50, y);
                doc.text(order.quantity + '', 100, y);
                doc.text(getOrderTotal(store.columnsList[order.id][2], order.quantity, store?.currency), 150, y);

                y += 5;
            })

            // customers
            y += 8
            doc.setFontSize(12);
            doc.text('Customers', 10, y);

            y += 5
            doc.setFontSize(10);
            doc.setFont(undefined, "bold");
            doc.text("Name", 10, y);
            doc.text("Email", 50, y);
            doc.text("Phone Number", 100, y);

            y += 5
            doc.setFont(undefined, "normal");
            doc.setFontSize(10);
            getCustomerAndOrders(user.uid, storeId).then((res) => {
                res.map(({customer}) => {
                    doc.text(customer.name, 10, y);
                    doc.text(customer.email, 50, y);
                    doc.text(customer.phone, 100, y);

                    y += 5
                })

                y += 8
                doc.setFontSize(12);
                doc.text('Customer Orders', 10, y);

                doc.setFontSize(10);
                y += 8
                res.map(({customer, orders}) => {
                    doc.setFont(undefined, "bold");
                    doc.text("Customer: " + customer.name, 10, y);

                    y += 8
                    doc.text("Ref", 10, y);
                    doc.text("Product", 50, y);
                    doc.text("Quantity", 100, y);
                    doc.text("Price", 150, y);

                    y += 5
                    doc.setFont(undefined, "normal");
                    orders.map((order) => {
                        doc.text(order.ref + '', 10, y);
                        doc.text(store.columnsList[order.id][1], 50, y);
                        doc.text(order.quantity + '', 100, y);
                        doc.text(store.columnsList[order.id][2] + ' ' + store?.currency, 150, y);

                        y += 5
                    })
                    y += 5
                })

                y += 5
                doc.setFontSize(12);
                doc.text('Packing List', 10, y);
                y += 8
                doc.setFontSize(10);
                doc.setFont(undefined, "bold");
                doc.text("Ref", 10, y);
                doc.text("Product", 50, y);
                doc.text("Quantity", 100, y);

                y += 5
                doc.setFontSize(10);
                doc.setFont(undefined, "normal");
                getPackingPageOrders(user.uid, storeId).then((res) => {
                    res.map((order) => {
                        doc.text(order.ref + '', 10, y);
                        doc.text(store.columnsList[order.id][1], 50, y);
                        doc.text(order.quantity + '', 100, y);

                        y += 5
                    })

                    // Save the PDF file
                    doc.save("popstore.pdf");
                    const blob = doc.output("blob");
                    // saveAs(blob, "postore.pdf");

                    handleAlert("success", "Download Completed.");
                })
            })
        }).catch((error) => console.log(error))
    };
    const deleteAndDownload = (e) => {
        const doc = new jsPDF();
        // Add store name and owner
        doc.setFontSize(18);
        doc.text(store.storeName, 10, 20);

        doc.setFontSize(12);
        doc.text(`Owner: ${store.storeOwner}`, 10, 30);

        doc.text("Products", 10, 40);
        // Add table headers
        doc.setFontSize(10);
        doc.setTextColor("#000");
        doc.setFont(undefined, "bold");
        doc.text("Ref", 10, 45);
        doc.text("Name", 50, 45);
        doc.text("Price", 100, 45);

        // Add the table rows
        let y = 50;
        store.columnsList.forEach((row) => {
            doc.setFontSize(10);
            doc.setTextColor("#000");
            doc.setFont(undefined, "normal");
            doc.text(row[0], 10, y);
            doc.text(row[1], 50, y);
            doc.text(row[2], 100, y);
            y += 5;
        });

        y += 8
        doc.setFontSize(12);
        doc.text('Orders', 10, y);

        y += 5
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text("Product", 10, y);
        doc.text("Price", 50, y);
        doc.text("Quantity", 100, y);
        doc.text("Total", 150, y);

        y += 5
        doc.setFont(undefined, "normal");
        getOrderService(user.uid, storeId).then((res) => {
            res.map((order, index) => {
                doc.text(store.columnsList[order.id][1], 10, y);
                doc.text(store.columnsList[order.id][2] + ' ' + store?.currency, 50, y);
                doc.text(order.quantity + '', 100, y);
                doc.text(getOrderTotal(store.columnsList[order.id][2], order.quantity, store?.currency), 150, y);

                y += 5;
            })

            // customers
            y += 8
            doc.setFontSize(12);
            doc.text('Customers', 10, y);

            y += 5
            doc.setFontSize(10);
            doc.setFont(undefined, "bold");
            doc.text("Name", 10, y);
            doc.text("Email", 50, y);
            doc.text("Phone Number", 100, y);

            y += 5
            doc.setFont(undefined, "normal");
            doc.setFontSize(10);
            getCustomerAndOrders(user.uid, storeId).then((res) => {
                res.map(({customer}) => {
                    doc.text(customer.name, 10, y);
                    doc.text(customer.email, 50, y);
                    doc.text(customer.phone, 100, y);

                    y += 5
                })

                y += 8
                doc.setFontSize(12);
                doc.text('Customer Orders', 10, y);

                doc.setFontSize(10);
                y += 8
                res.map(({customer, orders}) => {
                    doc.setFont(undefined, "bold");
                    doc.text("Customer: " + customer.name, 10, y);

                    y += 8
                    doc.text("Ref", 10, y);
                    doc.text("Product", 50, y);
                    doc.text("Quantity", 100, y);
                    doc.text("Price", 150, y);

                    y += 5
                    doc.setFont(undefined, "normal");
                    orders.map((order) => {
                        doc.text(order.ref + '', 10, y);
                        doc.text(store.columnsList[order.id][1], 50, y);
                        doc.text(order.quantity + '', 100, y);
                        doc.text(store.columnsList[order.id][2] + ' ' + store?.currency, 150, y);

                        y += 5
                    })
                    y += 5
                })

                y += 5
                doc.setFontSize(12);
                doc.text('Packing List', 10, y);
                y += 8
                doc.setFontSize(10);
                doc.setFont(undefined, "bold");
                doc.text("Ref", 10, y);
                doc.text("Product", 50, y);
                doc.text("Quantity", 100, y);

                y += 5
                doc.setFontSize(10);
                doc.setFont(undefined, "normal");
                getPackingPageOrders(user.uid, storeId).then((res) => {
                    res.map((order) => {
                        doc.text(order.ref + '', 10, y);
                        doc.text(store.columnsList[order.id][1], 50, y);
                        doc.text(order.quantity + '', 100, y);

                        y += 5
                    })

                    // Save the PDF file
                    doc.save("popstore.pdf");
                    const blob = doc.output("blob");
                    // saveAs(blob, "postore.pdf");

                    handleAlert("success", "Download Completed.").then(() => {
                        handleDeletePopup()
                    });
                })
            })
        }).catch((error) => console.log(error))
    };

    if (loading) return <Loading/>;
    return (
        <Container maxWidth="lg">
            {/* Top Container */}
            <>
                <div style={{
                    width: "100%",
                    display: "flex",
                    gap: 40,
                    justifyContent: "end",
                    alignItems: "center"
                }}>
                    <Button
                        variant="outlined"
                        sx={{
                            border: "1px solid #353535",
                            color: "#353535",
                            minWidth: "128px",
                            fontSize: 16,
                            fontWeight: 400,
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleCopy(`/store/${user.uid}/${storeId}`)}>
                        <ContentCopyIcon/>&nbsp;{t("Copy Link")}
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{
                            border: "1px solid #353535",
                            color: "#353535",
                            minWidth: "169px",
                            fontSize: 16,
                            fontWeight: 400,
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => {
                            generatePDF()
                        }}>
                        <FileDownloadOutlinedIcon/>&nbsp;{t("Download")}
                    </Button>
                    <div>
                        <Button
                            variant="contained"
                            sx={{width: "169px", fontSize: 16, fontWeight: 400,}}
                            onClick={updateStore}>
                            {t("Update")}
                        </Button>
                        {alert.type === "error" && (
                            <ErrorAlert
                                open={openModal}
                                onClose={handleCloseModal}
                                message={alert.message}
                            />
                        )}
                        {alert.type === "success" && (
                            <SuccessAlert
                                open={openModal}
                                onClose={handleCloseModal}
                                message={alert.message}
                                navigate={handleCloseModal}
                            />
                        )}
                        {alert.type === "delete" && (
                            <DeleteAlert
                                open={openModal}
                                onClose={handleCloseModal}
                                message={alert.message}
                                navigate={handleCloseModal}
                                deleteClicked={() => handleDelete()}
                                cancelClicked={handleCloseModal}
                                downloadClicked={deleteAndDownload}
                            />
                        )}
                    </div>
                    <Link href="#" sx={{
                        width: "50px",
                        fontSize: 16,
                        color: '#353535',
                        fontWeight: 400,
                        textDecorationColor: '#353535'
                    }} onClick={cancelStore}>
                        {t("Cancel")}
                    </Link>
                </div>
            </>

            {/* Second Container */}
            <Grid container spacing={2} sx={{marginTop: 3}}>
                <Grid item xs={12} md={7}>
                    <Grid item xs={12} md={12} style={{display: "flex", gap: "49px", flexWrap: "wrap"}}>
                        <div style={{width: "249px"}}>
                            <div>
                                <Typography variant="body" color="text.main">
                                    {t("Store Name")}
                                </Typography>
                            </div>
                            <StyledTextField
                                className={'custom-input'}
                                fullWidth
                                id="outlined-basic"
                                label=""
                                helperText=""
                                variant="outlined"
                                value={store.storeName}
                                onChange={(e) =>
                                    setStore({...store, storeName: e.target.value})
                                }
                            />
                        </div>
                        <div style={{width: "249px"}}>
                            <div>
                                <Typography variant="body" color="text.main">
                                    {t("Contact")}
                                </Typography>
                            </div>
                            <StyledTextField className={'custom-input'}
                                             fullWidth
                                             id="outlined-basic"
                                             label=""
                                             helperText=""
                                             type="email"
                                             variant="outlined"
                                             value={store.storeOwner}
                                             onChange={(e) =>
                                                 setStore({...store, storeOwner: e.target.value})
                                             }
                            />
                        </div>
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    xs={12}
                    md={5}
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={"20px"}
                >
                    <p style={{
                        fontFamily: 'Open Sans',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        fontSize: '16px',
                        lineHeight: '22px',
                        color: '#353535',
                        paddingBottom: "3px"
                    }}>
                        {t('Lock store')}
                    </p>
                    <div style={{cursor: "pointer"}}>
                        {
                            lockStore ?
                                <span onClick={(e) => setLockStore(false)}>
                                    <ToggleOn/>
                                </span> :
                                <span onClick={(e) => setLockStore(true)}>
                                    <ToggleOff/>
                                </span>

                        }
                    </div>
                </Grid>
                <Grid item xs={12} md={6}>
                    <div>
                        <Typography variant="body" color="text.main">
                            {t("Description")}
                        </Typography>
                    </div>
                    <StyledTextField className={'custom-textarea'}
                                     style={{width: "547px"}}
                                     multiline
                                     fullWidth
                                     id="outlined-basic"
                                     label=""
                                     helperText=""
                                     variant="outlined"
                                     value={store.description}
                                     onChange={(e) =>
                                         setStore({...store, description: e.target.value})
                                     }
                                     inputProps={{
                                         style: {
                                             whiteSpace: "pre-wrap",
                                             wordWrap: "break-word",
                                             width: "547px"
                                         },
                                     }}
                    />
                </Grid>
                <Grid container justifyContent="flex-end" xs={12} md={6}>
                    <Grid sx={{
                        marginTop: "14px"
                    }} item width="50%">
                        <div>
                            <Typography variant="body" color="text.main">
                                {t("Currency")}
                            </Typography>
                        </div>
                         <Select
                            className={'custom-select'}
                            fullWidth={true}
                            label=""
                            value={eurocurrencies[store.currency] !== undefined ? 'Euro': store.currency}
                            onChange={(e) => setStore({...store, currency: e.target.value})}
                            id="currency"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: "#fff",
                                        maxHeight: "200px",
                                        "& .MuiMenuItem-root": {
                                            padding: 2,
                                        },
                                    },
                                },
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                getContentAnchorEl: null,
                            }}
                            sx={{
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "1px solid",
                                    borderColor: "#353535",
                                    borderRadius: "6px",
                                },
                                "& .MuiOutlinedInput-root": {
                                    "&:hover": {
                                        borderColor: (theme) => theme.palette.primary.main,
                                    },
                                    "&:focused": {
                                        borderColor: (theme) => theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            {Object.keys(eurocurrencies).map((currency, i) => (
                                <MenuItem key={`${i}`} value={eurocurrencies[currency]}
                                    onClick={(e) =>
                                        updateCurrencyColumn(e, currency, eurocurrencies[currency])
                                    }>
                                    {currency} - {eurocurrencies[currency]}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                </Grid>
            </Grid>
            {/* Third Container */}
            {/* Fourth Container */}

            <div className="create-table-wrapper">
                <TableContainer
                    sx={{backgroundColor: (theme) => theme.palette.background2}}
                >
                    <Table id="table" style={{tableLayout: "fixed"}}>
                        <TableHead sx={{background: (theme) => theme.palette.primary.main}}>
                            <TableRow>
                                <TableCell style={{paddingLeft: "42px"}} className={'table-head'} sx={{
                                    color: (theme) => theme.palette.white.main,
                                    width: "315px"
                                }}>
                                    {t("Reference ID")}
                                </TableCell>
                                <TableCell className={'table-head'} sx={{
                                    color: (theme) => theme.palette.white.main,
                                    width: "270px"
                                }}>
                                    {t("Name")}
                                </TableCell>
                                <TableCell className={'table-head'} sx={{
                                    color: (theme) => theme.palette.white.main
                                }}>
                                    {t("Price")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {store.columnsList?.map((row, rowIndex) => {
                                return (
                                    <TableRow
                                        key={`row-${rowIndex}`}
                                        sx={{
                                            borderTop: "2px solid #4C8991",
                                            borderColor: (theme) =>
                                                alpha(theme.palette.primary.main, 0.3),
                                            "&:first-child": {
                                                borderTop: "none",
                                            },
                                            "&:last-child": {
                                                borderBottom: "solid 2px white",
                                            },
                                        }}>
                                        {row.map((cell, cellIndex) =>
                                            cellIndex !== 3 ? (
                                                <TableCell
                                                    style={{
                                                        fontFamily: 'Open Sans',
                                                        fontStyle: 'normal',
                                                        fontWeight: 400,
                                                        fontSize: '14px',
                                                        lineHeight: '16px',
                                                        color: '#353535',
                                                        paddingLeft: cellIndex === 0 ? "42px" : "",
                                                    }}
                                                    key={`cell-${rowIndex}-${cellIndex}`}
                                                    className={styles["cell"]}>
                                                    {cell} {`${cellIndex === 2 ? store.currency : ''}`}
                                                </TableCell>
                                            ) : (
                                                ""
                                            )
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <Grid container xs={12} md={12} justifyContent="flex-end" marginTop={3}>
                <p
                    style={{
                        color: "red",
                        width: isMobile ? "100%" : "20%", fontSize: 16,
                        fontStyle: "normal",
                        fontWeight: 400,
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => handleDeletePopup()}>
                    <svg width="20" height="26" viewBox="0 0 20 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M18.2015 9.19303L17.8015 21.3C17.7674 22.3369 17.3306 23.3197 16.5838 24.0398C15.837 24.76 14.8389 25.1607 13.8015 25.157H6.20149C5.16471 25.1607 4.16725 24.7606 3.42054 24.0413C2.67383 23.322 2.23658 22.3402 2.20149 21.304L1.80149 9.19303C1.79274 8.92781 1.8897 8.66998 2.07105 8.47626C2.2524 8.28253 2.50328 8.16878 2.76849 8.16003C3.03371 8.15128 3.29154 8.24824 3.48526 8.42959C3.67899 8.61094 3.79274 8.86181 3.80149 9.12703L4.20149 21.237C4.22141 21.754 4.44088 22.2431 4.81382 22.6017C5.18676 22.9603 5.68413 23.1604 6.20149 23.16H13.8015C14.3195 23.1604 14.8175 22.9597 15.1905 22.6003C15.5636 22.2409 15.7826 21.7507 15.8015 21.233L16.2015 9.12703C16.2102 8.86181 16.324 8.61094 16.5177 8.42959C16.7114 8.24824 16.9693 8.15128 17.2345 8.16003C17.4997 8.16878 17.7506 8.28253 17.9319 8.47626C18.1133 8.66998 18.2102 8.92781 18.2015 9.19303ZM19.5245 5.16403C19.5245 5.42925 19.4191 5.6836 19.2316 5.87114C19.0441 6.05867 18.7897 6.16403 18.5245 6.16403H1.47949C1.21428 6.16403 0.959921 6.05867 0.772385 5.87114C0.584849 5.6836 0.479492 5.42925 0.479492 5.16403C0.479492 4.89881 0.584849 4.64446 0.772385 4.45692C0.959921 4.26939 1.21428 4.16403 1.47949 4.16403H4.57949C4.89634 4.16488 5.20217 4.04782 5.43747 3.83563C5.67277 3.62343 5.82071 3.33128 5.85249 3.01603C5.92629 2.27652 6.27275 1.59096 6.82439 1.09294C7.37602 0.594916 8.09331 0.320098 8.83649 0.322032H11.1665C11.9097 0.320098 12.627 0.594916 13.1786 1.09294C13.7302 1.59096 14.0767 2.27652 14.1505 3.01603C14.1823 3.33128 14.3302 3.62343 14.5655 3.83563C14.8008 4.04782 15.1066 4.16488 15.4235 4.16403H18.5235C18.7887 4.16403 19.0431 4.26939 19.2306 4.45692C19.4181 4.64446 19.5235 4.89881 19.5235 5.16403H19.5245ZM7.58849 4.16403H12.4165C12.2851 3.86377 12.1991 3.54562 12.1615 3.22003C12.1367 2.97354 12.0213 2.74502 11.8377 2.57874C11.654 2.41247 11.4152 2.32028 11.1675 2.32003H8.83749C8.58976 2.32028 8.35093 2.41247 8.16729 2.57874C7.98365 2.74502 7.86827 2.97354 7.84349 3.22003C7.80552 3.54567 7.72024 3.86383 7.58849 4.16403ZM8.59549 19.315V10.8C8.59549 10.5348 8.49013 10.2805 8.3026 10.0929C8.11506 9.90539 7.86071 9.80003 7.59549 9.80003C7.33028 9.80003 7.07592 9.90539 6.88839 10.0929C6.70085 10.2805 6.59549 10.5348 6.59549 10.8V19.319C6.59549 19.5842 6.70085 19.8386 6.88839 20.0261C7.07592 20.2137 7.33028 20.319 7.59549 20.319C7.86071 20.319 8.11506 20.2137 8.3026 20.0261C8.49013 19.8386 8.59549 19.5842 8.59549 19.319V19.315ZM13.4095 19.315V10.8C13.4095 10.5348 13.3041 10.2805 13.1166 10.0929C12.9291 9.90539 12.6747 9.80003 12.4095 9.80003C12.1443 9.80003 11.8899 9.90539 11.7024 10.0929C11.5148 10.2805 11.4095 10.5348 11.4095 10.8V19.319C11.4095 19.5842 11.5148 19.8386 11.7024 20.0261C11.8899 20.2137 12.1443 20.319 12.4095 20.319C12.6747 20.319 12.9291 20.2137 13.1166 20.0261C13.3041 19.8386 13.4095 19.5842 13.4095 19.319V19.315Z"
                            fill="#F30A0A"/>
                    </svg>
                    &nbsp;&nbsp;&nbsp;
                    <span style={{textDecorationLine: "underline"}}>{t("Delete store")}</span>
                </p>
            </Grid>
            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}>
                <MuiAlert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    sx={{
                        backgroundColor: "#656f71",
                        color: "#FFF",
                        "& .MuiAlert-icon": {
                            color: "#FFF",
                        },
                    }}
                >
                    {t("Link copied")}
                </MuiAlert>
            </Snackbar>
        </Container>
    );
};

export default EditPopstore;
